import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { NavController, Platform } from '@ionic/angular';
import { IDEAStorageService } from '@idea-ionic/common';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { UserService } from '@tabs/user/user.service';

import { User } from '@models/user.model';

export const authGuard: CanActivateFn = async (): Promise<boolean> => {
  const platform = inject(Platform);
  const navCtrl = inject(NavController);
  const storage = inject(IDEAStorageService);
  const auth = inject(AuthService);
  const _user = inject(UserService);
  const app = inject(AppService);

  if (app.authReady) return true;

  //
  // HELPERS
  //

  const doAuth = async (): Promise<void> => {
    const token = await auth.loadAuthToken();
    if (!token) throw new Error('Missing auth token');
    const { user, configurations } = await _user.getCurrentUserAndConfiguration();
    app.user = user.hasAcceptedCurrentLegalDocuments() ? user : await recordLegalAcceptance();
    app.configurations = configurations;
  };

  /**
   * Record the agreement on the legal documents given in the auth page; without one (e.g. the documents changed
   * since the last sign-in) the user goes back to the auth page, to read and agree on the current versions.
   */
  const recordLegalAcceptance = async (): Promise<User> => {
    const versions = await auth.consumePendingLegalAcceptance();
    if (!versions) {
      await auth.removeAuthToken();
      throw new Error('Legal documents not accepted');
    }
    return await _user.acceptLegalDocuments(versions);
  };

  const navigateAndResolve = (navigationPath?: string[]): boolean => {
    if (navigationPath) navCtrl.navigateRoot(navigationPath);
    app.authReady = true;
    return true;
  };

  //
  // MAIN
  //

  if (app.authReady) return true;

  await platform.ready();
  await storage.ready();

  try {
    await doAuth();
    platform.resume.subscribe((): Promise<void> => doAuth());

    if (window.location.pathname === '/') return navigateAndResolve([]);
    else return navigateAndResolve();
  } catch (err) {
    return navigateAndResolve(['auth']);
  }
};
